import React from 'react'
import { getStatusColor } from 'constants/status'
import { request } from 'utilities/request'
import { showDate } from 'utilities/form'
import { textFilter, dateFilter, selectFilter } from 'utilities/filter'
import { getLimit, getPagination } from 'utilities/pagination'
import { LinkButton, StatusChip, Link } from 'components/core'
import { MdEdit } from 'react-icons/md'
import { renderDeleteButton } from 'utilities/app'
import { handleDelete } from 'actions/ticket'
import { FILTER_SET } from 'constants/actionType'
import { getAdjustTypes } from './adjustPage'

export const filters = (locations) => [
  {
    id: 'id',
    label: 'field.ticketId',
    input: textFilter,
  },
  {
    id: 'type',
    label: 'adjust.field.type',
    input: (props) =>
      selectFilter({
        ...props,
        options: getAdjustTypes(props.message),
      }),
  },
  {
    id: 'transDate',
    label: 'field.date',
    input: dateFilter,
  },
  {
    id: 'sku',
    label: 'field.sku',
    input: textFilter,
  },
  {
    id: 'toLocationId',
    label: 'adjust.field.location',
    input: (props) =>
      selectFilter({
        ...props,
        isSearchable: true,
        options: getLocationOptions(locations, props.message),
      }),
  },
]

export function getLocationOptions(locations, message) {
  if (!locations) return []
  const warehouses = []
  const techs = []
  locations.forEach(({ type, id, name }) => {
    const result = { label: name, value: id }

    switch (type) {
      case 'PART_WAREHOUSE':
        warehouses.push(result)
        break
      case 'TECHNICIAN':
        techs.push(result)
        break
      default:
    }
  })
  return [
    { label: message({ id: 'location.type.WAREHOUSE' }), options: warehouses },
    { label: message({ id: 'location.type.TECHNICIAN' }), options: techs },
  ]
}

export const columns = ({ session, app, message, action }) => [
  {
    id: 'id',
    label: 'field.ticketId',
    format: ['html', 'excel'],
    renderHtml: ({ row }) => (
      <Link variant="primaryLink" href={`/adjust/${row.id}/view`}>
        {row.id}
      </Link>
    ),
  },
  {
    id: 'transDate',
    label: 'field.date',
    format: ['html', 'excel'],
    render: ({ row }) => showDate(row.transDate),
  },
  {
    id: 'toLocationName',
    label: 'adjust.field.location',
    format: ['html', 'excel'],
  },
  {
    id: 'adjustType',
    label: 'adjust.field.type',
    format: ['html', 'excel'],
    render: ({ row }) => message({ id: `adjust.type.${row.extra?.type}` }),
  },
  {
    id: 'memo',
    label: 'field.memo',
    format: ['excel'],
  },
  {
    id: 'productVariantName',
    label: 'field.partName',
    format: ['excel'],
  },
  {
    id: 'sku',
    label: 'field.sku',
    format: ['excel'],
  },
  {
    id: 'quantity',
    label: 'field.quantity',
    format: ['excel'],
  },
  {
    id: 'status',
    label: 'field.status',
    format: ['html', 'excel'],
    renderHtml: ({ row }) => (
      <StatusChip
        label={`status.${row.status}`}
        color={getStatusColor(row.status)}
      />
    ),
    render: ({ row }) => message({ id: `status.${row.status}` }),
  },
  {
    id: 'actions',
    align: 'right',
    renderHtml: ({ row }) => (
      <>
        <LinkButton
          mr={2}
          disabled={row.status === 'INACTIVE'}
          variant="icon"
          icon={<MdEdit />}
          href={`/adjust/${row.id}/edit`}
        />
        {renderDeleteButton({
          module: 'adjust',
          session,
          status: row.status,
          onSubmit: () =>
            handleDelete('partAdjust', {
              session,
              app,
              id: row.id,
              hash: row.hash,
              action,
            }),
        })}
      </>
    ),
  },
]

export const handlers = ({
  session,
  app,
  message,
  setState,
  setFilterValues,
  setPagination,
  setLocations,
}) => ({
  handleLoad: async ({ pagination, filterValues } = {}) => {
    const filterId = 'adjustList'
    if (!filterValues) {
      filterValues = session.state.filterMap[filterId] || []
      pagination = session.state.paginationMap[filterId] || []
    }
    const resp = await getData({ session, app, pagination, filterValues })
    setFilterValues(filterValues)
    setState(resp.data)
    setPagination(resp.pagination)
    setLocations(resp.locations)
    session.dispatch({
      type: FILTER_SET,
      key: filterId,
      filterValues,
      pagination,
    })
  },
  handlerExport: async ({ filterValues, createExcel }) => {
    const title = 'adjust.title.list'
    const cols = columns({ message }).filter(({ format = [] }) =>
      format.includes('excel'),
    )
    const rows = await getExportData({ session, app, filterValues })
    createExcel({ message, title, cols, rows })
  },
})

async function getData({ session, app, pagination = {}, filterValues = [] }) {
  const variables = {
    locationInput: { type: ['PART_WAREHOUSE', 'TECHNICIAN'] },
    input: getDataInput({ pagination, filterValues }),
  }
  const query = `
    query($input: TicketQueryInput, $locationInput: LocationQueryInput) {
      locations(input: $locationInput) {
        id
        name
        type
      }
      partAdjustTicketCount(input: $input)
      partAdjustTickets(input: $input) {
        id
        ticketNo
        toLocationName
        transDate
        extra
        status
        hash
      }
    }
  `
  const [ok, data] = await request({ query, variables }, { session, app })
  if (!ok) return {}

  // const locations = filterLocations(app.state.staff, data.locations)
  const { page, countPerPage } = pagination
  return {
    data: data.partAdjustTickets,
    pagination: getPagination(page, countPerPage, data.partAdjustTicketCount),
    locations: data.locations,
  }
}

async function getExportData({ session, app, filterValues }) {
  const variables = { input: getDataInput({ filterValues, joinItem: true }) }
  const query = `
    query($input: TicketQueryInput) {
      partAdjustTickets(input: $input) {
        id
        ticketNo
        toLocationName
        productVariantName
        sku
        quantity
        extra
        transDate
        status
      }
    }
  `
  const [ok, data] = await request({ query, variables }, { session, app })
  if (!ok) return []

  return data.partAdjustTickets.map((item) => {
    const { extra } = item
    item.billingName = extra.billCompany?.name
    item.memo = extra.memo
    return item
  })
}

function getDataInput({ filterValues, pagination, joinItem = false }) {
  const id = filterValues.find(({ id }) => id === 'id')
  const type = filterValues.find(({ id }) => id === 'type')
  const transDate = filterValues.find(({ id }) => id === 'transDate')
  const sku = filterValues.find(({ id }) => id === 'sku')
  const toLocationId = filterValues.find(({ id }) => id === 'toLocationId')
  const input = {
    joinItem,
    id: id?.value,
    transDate: transDate?.value,
    sku: sku?.value,
    toLocationId: toLocationId?.value.value,
    extra: {
      type: type?.value.value,
    },
    orderBy: [{ key: 'id', sort: 'DESC' }],
  }
  if (pagination) input.limit = getLimit(pagination)
  return input
}
